import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectApi } from '../services/api';
import { useProject } from '../App';
import { formatTimestamp } from '../utils/format';

function OperationTypeBadge({ type }) {
    const colors = {
        CREATE: 'bg-blue-100 text-blue-800',
        DEPLOY_CONNECTOR: 'bg-purple-100 text-purple-800',
        REMOVE_CONNECTOR: 'bg-purple-100 text-purple-800',
        START_CONSUMER: 'bg-green-100 text-green-800',
        STOP_CONSUMER: 'bg-yellow-100 text-yellow-800',
        UPDATE: 'bg-indigo-100 text-indigo-800',
        DELETE: 'bg-red-100 text-red-800',
    };
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
            {type}
        </span>
    );
}

function OperationRow({ op }) {
    const [showDetail, setShowDetail] = useState(false);
    return (
        <tr className="border-b hover:bg-gray-50">
            <td className="px-4 py-3 text-sm">{formatTimestamp(op.timestamp)}</td>
            <td className="px-4 py-3 text-sm font-medium">{op.projectName}</td>
            <td className="px-4 py-3"><OperationTypeBadge type={op.operation} /></td>
            <td className="px-4 py-3"><StatusBadge status={op.status} /></td>
            <td className="px-4 py-3 text-sm text-gray-600 max-w-xs relative"
                onMouseEnter={() => setShowDetail(true)}
                onMouseLeave={() => setShowDetail(false)}>
                <span className="block truncate">{op.detail}</span>
                {showDetail && op.detail && (
                    <div style={{position:'absolute', left:0, top:'100%', paddingTop:'4px', zIndex:9999}}
                         onMouseEnter={() => setShowDetail(true)}
                         onMouseLeave={() => setShowDetail(false)}>
                        <div style={{background:'#1f2937', color:'white', padding:'12px', borderRadius:'6px', fontSize:'13px', minWidth:'400px', maxWidth:'700px', whiteSpace:'normal', wordBreak:'break-word', boxShadow:'0 10px 15px -3px rgba(0,0,0,0.3)', userSelect:'all'}}>
                            {op.detail}
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
}

function StatusBadge({ status }) {
    return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {status}
        </span>
    );
}

function OperationHistory() {
    const [searchParams] = useSearchParams();
    const { projects } = useProject();
    const [operations, setOperations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
    const [selectedType, setSelectedType] = useState('');
    const size = 20;

    const loadOperations = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            if (selectedProject) {
                res = await projectApi.getProjectOperations(selectedProject, page, size);
            } else {
                res = await projectApi.getAllOperations(page, size);
            }
            let content = res.data?.content || [];
            if (selectedType) {
                content = content.filter((op) => op.operation === selectedType);
            }
            setOperations(content);
            setTotalPages(res.data?.totalPages || 0);
        } catch (err) {
            console.error('Failed to load operations:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedProject, selectedType, page]);

    useEffect(() => {
        loadOperations();
    }, [loadOperations]);

    return (
        <div>
            <h1 className="text-2xl font-bold mb-4">Operation History</h1>

            <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="flex space-x-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Project</label>
                        <select value={selectedProject} onChange={(e) => { setSelectedProject(e.target.value); setPage(0); }}
                            className="border rounded px-3 py-2 text-sm">
                            <option value="">All Projects</option>
                            {projects.map((p) => (
                                <option key={p.name} value={p.name}>{p.displayName || p.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Operation Type</label>
                        <select value={selectedType} onChange={(e) => { setSelectedType(e.target.value); setPage(0); }}
                            className="border rounded px-3 py-2 text-sm">
                            <option value="">All Types</option>
                            <option value="CREATE">Create</option>
                            <option value="DEPLOY_CONNECTOR">Deploy Connector</option>
                            <option value="REMOVE_CONNECTOR">Remove Connector</option>
                            <option value="START_CONSUMER">Start Consumer</option>
                            <option value="STOP_CONSUMER">Stop Consumer</option>
                            <option value="UPDATE">Update</option>
                            <option value="DELETE">Delete</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : operations.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No operations found</div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="border-b text-left text-gray-500 text-sm">
                                <th className="px-4 py-3">Time</th>
                                <th className="px-4 py-3">Project</th>
                                <th className="px-4 py-3">Operation</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {operations.map((op) => (
                                <OperationRow key={op.id} op={op} />
                            ))}
                        </tbody>
                    </table>
                )}

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
                        <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50">
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OperationHistory;
